import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FolderPlus, Megaphone, Users, FileText, MessagesSquare, Shield, Star, Archive, Ticket, Trash2, Activity, Bot, DollarSign, AlertTriangle, Eye, Mail, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ImageUploader } from "@/components/ImageUploader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { YouTubeSync } from "@/components/YouTubeSync";
import { DigestCard } from "@/components/DigestCard";
import { AcademyAdmin } from "@/components/AcademyAdmin";
import { VideoLibraryAdmin } from "@/components/VideoLibraryAdmin";
import { caseDisplayName } from "@/lib/caseName";


function AssistantActivityCard() {
  const [hold, setHold] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, a] = await Promise.all([
        api.get("/admin/assistant-settings"),
        api.get("/admin/assistant/activity"),
      ]);
      setHold(!!s.data.hold_for_approval);
      setItems(a.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const toggleHold = async () => {
    setBusy(true);
    try {
      const r = await api.put("/admin/assistant-settings", { hold_for_approval: !hold });
      setHold(r.data.hold_for_approval);
      toast.success(r.data.hold_for_approval ? "Assistant posts now wait for your approval" : "Assistant posts go live automatically");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const approve = async (it) => {
    try {
      if (it.type === "announcement") await api.post(`/admin/announcements/${it.id}/approve`);
      else await api.patch(`/cases/${it.id}`, { published: true, status: "Active" });
      toast.success("Approved — now live"); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const undo = async (it) => {
    try {
      if (it.type === "announcement") await api.delete(`/admin/announcements/${it.id}`);
      else await api.patch(`/cases/${it.id}`, { published: false, status: "draft" });
      toast.success(it.type === "announcement" ? "Announcement removed" : "Case unpublished"); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="mb-10" data-testid="assistant-activity-panel">
      <div className="flex items-center gap-2 mb-4"><Bot className="w-4 h-4 text-gold" /><h2 className="label-mono">Community Assistant Activity</h2><span className="label-mono text-white/35 normal-case">· last 2 weeks</span></div>

      <div className="card-surface rounded-md p-4 mb-4 flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-white">Hold for my approval</div>
          <div className="text-xs text-white/50 mt-0.5">When on, the Assistant's new cases & announcements wait here as <span className="text-amber-300">Pending</span> instead of going live.</div>
        </div>
        <button onClick={toggleHold} disabled={busy} data-testid="assistant-hold-toggle"
          className={`relative w-14 h-7 rounded-full transition-colors shrink-0 ${hold ? "bg-gold" : "bg-white/15"}`}>
          <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-charcoal transition-transform ${hold ? "translate-x-7" : ""}`} />
        </button>
      </div>

      {items.length === 0 ? (
        <div className="card-surface rounded-md p-6 text-center text-white/50 text-sm" data-testid="assistant-activity-empty">Nothing posted by the Assistant yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={`${it.type}-${it.id}`} className="card-surface rounded-md p-3 flex items-center justify-between gap-3" data-testid="assistant-activity-row">
              <div className="flex items-center gap-3 min-w-0">
                {it.type === "announcement" ? <Megaphone className="w-4 h-4 text-gold shrink-0" /> : <FolderPlus className="w-4 h-4 text-gold shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white truncate">{it.title}</div>
                  <div className="label-mono text-white/40">
                    {it.type}{it.auto ? " · auto-welcome" : ""}{it.created_at ? ` · ${new Date(it.created_at).toLocaleDateString()}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm ${it.status === "pending" ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}>{it.status}</span>
                {it.type === "case" && it.slug && <Link to={`/cases/${it.slug}`} className="text-xs px-2.5 py-1.5 rounded-sm border border-white/10 text-white/70 hover:border-gold/40">Open</Link>}
                {it.status === "pending" && (
                  <button onClick={() => approve(it)} data-testid="assistant-approve-btn" className="text-xs px-2.5 py-1.5 rounded-sm bg-emerald-500 text-charcoal font-semibold hover:bg-emerald-400">Approve</button>
                )}
                <button onClick={() => undo(it)} data-testid="assistant-undo-btn" className="text-xs px-2.5 py-1.5 rounded-sm border border-white/10 text-white/60 hover:text-red-400 hover:border-red-400/40 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> {it.type === "announcement" ? "Remove" : "Unpublish"}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CompMemberCard() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async (action) => {
    if (!email.trim()) { toast.error("Enter the member's email"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/members/comp", { email: email.trim(), action });
      setResult(data);
      toast.success(action === "revoke"
        ? `Removed free access for ${data.name || data.email}`
        : `${data.name || data.email} now has full free access`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="card-surface rounded-md p-5 mb-6" data-testid="comp-member-card">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-4 h-4 text-gold" />
        <h3 className="font-display font-semibold text-white">Give a member free access</h3>
      </div>
      <p className="text-xs text-white/50 mb-3">Instantly make any account a full Founding Member — no coupon, no charge. Use this to restore a member who should have access. They must have created a free account first. Comped members never get dropped by billing sync.</p>
      <div className="flex flex-col sm:flex-row gap-2 max-w-md">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="member@email.com" data-testid="comp-member-email-input" className="bg-charcoal border-white/15" />
        <Button onClick={() => run("grant")} disabled={busy} data-testid="comp-member-grant-btn" className="shrink-0 bg-gold text-charcoal hover:bg-gold-hover">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Grant free access"}
        </Button>
        <Button onClick={() => run("revoke")} disabled={busy} data-testid="comp-member-revoke-btn" variant="outline" className="shrink-0 border-white/15 text-white/70 hover:border-red-400/40 hover:text-red-400 bg-transparent">
          Revoke
        </Button>
      </div>
      {result && (
        <p className="mt-3 text-sm text-gold" data-testid="comp-member-result">
          {result.status === "comped" ? `✓ ${result.name || result.email} is now a Founding Member (free).` : `✓ Free access removed for ${result.name || result.email}.`}
        </p>
      )}
    </div>
  );
}

function PastBullpensAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { case_name: "", title: "", date: "", description: "", issues: ["", "", ""], video_url: "", published: true };
  const [f, setF] = useState(empty);
  const load = () => api.get("/admin/past-bullpens").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setF({ ...empty, ...p, issues: [...(p.issues || []), "", "", ""].slice(0, 3) }); setOpen(true); };
  const setIssue = (i, v) => setF((s) => ({ ...s, issues: s.issues.map((x, k) => (k === i ? v : x)) }));
  const save = async () => {
    if (!f.case_name.trim() || !f.title.trim()) { toast.error("Case name and title are required"); return; }
    const payload = { ...f, issues: f.issues.filter((x) => x.trim()) };
    try {
      if (editing) await api.put(`/admin/past-bullpens/${editing}`, payload);
      else await api.post("/admin/past-bullpens", payload);
      setOpen(false); load(); toast.success("Past Bullpen saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/past-bullpens/${id}`); load(); toast.success("Deleted"); };
  return (
    <div className="card-surface rounded-md p-5 mb-6" data-testid="past-bullpens-admin">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2"><Archive className="w-4 h-4 text-gold" /><h3 className="font-display font-semibold text-white">Past Bullpens · Case Review Library</h3></div>
        <Button onClick={openNew} data-testid="add-past-bullpen-btn" className="bg-gold text-charcoal hover:bg-gold-hover rounded-sm text-sm">Add session</Button>
      </div>
      <p className="text-xs text-white/50 mb-3">Members-only archive of past Bullpen case reviews. The video link is never shown publicly — only logged-in members can play it.</p>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-white/40">No past Bullpens yet.</p>}
        {items.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-3 border border-white/10 rounded-sm p-3" data-testid={`admin-pb-${p.id}`}>
            <div className="min-w-0">
              <div className="text-sm text-white font-medium truncate">{p.case_name} — {p.title}</div>
              <div className="label-mono text-white/40">{p.date} · {p.published ? "Published" : "Hidden"} · {p.video_url ? "Video set" : "No video"}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => openEdit(p)} className="text-xs px-3 py-1.5 rounded-sm border border-white/10 text-white/60 hover:text-gold hover:border-gold/40 transition-colors" data-testid={`edit-pb-${p.id}`}>Edit</button>
              <button onClick={() => del(p.id)} className="text-white/30 hover:text-red-400 transition-colors" data-testid={`delete-pb-${p.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-charcoal border-white/15 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editing ? "Edit" : "Add"} Past Bullpen</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Case name (e.g. Tyler Robinson)" value={f.case_name} onChange={(e) => setF({ ...f, case_name: e.target.value })} className="bg-charcoal border-white/15" data-testid="pb-case-name" />
            <Input placeholder="Bullpen title (e.g. Did Police Follow Their Own Policies?)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="bg-charcoal border-white/15" data-testid="pb-title" />
            <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="bg-charcoal border-white/15" data-testid="pb-date" />
            <Textarea placeholder="Short description" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="bg-charcoal border-white/15" data-testid="pb-description" />
            {[0, 1, 2].map((i) => (
              <Input key={i} placeholder={`Key issue ${i + 1}`} value={f.issues[i] || ""} onChange={(e) => setIssue(i, e.target.value)} className="bg-charcoal border-white/15" data-testid={`pb-issue-${i}`} />
            ))}
            <Input placeholder="Unlisted YouTube URL (members only)" value={f.video_url} onChange={(e) => setF({ ...f, video_url: e.target.value })} className="bg-charcoal border-white/15" data-testid="pb-video-url" />
            <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="pb-published" /> Published (visible to members)</label>
          </div>
          <DialogFooter><Button onClick={save} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="save-pb-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FeaturedEventCard() {
  const [opts, setOpts] = useState([]);
  const [sel, setSel] = useState("auto");
  const [saved, setSaved] = useState("auto");
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/featured-event").then(({ data }) => {
    setOpts(data.options || []);
    const cur = data.mode === "pinned" && data.event_id ? data.event_id : "auto";
    setSel(cur); setSaved(cur);
  }).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const body = sel === "auto" ? { mode: "auto" } : { mode: "pinned", event_id: sel };
      const { data } = await api.put("/admin/featured-event", body);
      const cur = data.mode === "pinned" && data.event_id ? data.event_id : "auto";
      setSaved(cur); setSel(cur);
      toast.success("Featured event updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const fmt = (o) => {
    const d = o.date ? new Date(`${o.date}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
    return `${o.title}${d ? ` · ${d}` : ""}${o.time ? ` ${o.time}` : ""}`;
  };
  return (
    <div className="card-surface rounded-md p-5 mb-6" data-testid="featured-event-card">
      <div className="flex items-center gap-2 mb-1"><Star className="w-4 h-4 text-gold" /><h3 className="font-display font-semibold text-white">Featured event (homepage top)</h3></div>
      <p className="text-xs text-white/50 mb-3">Choose what shows in the big event block at the top of your homepage. Leave on <span className="text-white/70">Auto</span> to always show your next upcoming Bullpen or event, or pin a specific one.</p>
      <div className="flex flex-col sm:flex-row gap-2 max-w-2xl">
        <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid="featured-event-select"
          className="bg-charcoal border border-white/15 rounded-sm text-sm text-white px-3 py-2 flex-1">
          <option value="auto">Auto — next upcoming event</option>
          {opts.map((o) => (<option key={o.event_id} value={o.event_id}>{fmt(o)}</option>))}
        </select>
        <Button onClick={save} disabled={saving || sel === saved} data-testid="featured-event-save-btn" className="shrink-0 bg-gold text-charcoal hover:bg-gold-hover">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
      {opts.length === 0 && <p className="text-[11px] text-white/35 mt-2">No upcoming events to pin right now — Auto will hide the block until one is scheduled.</p>}
    </div>
  );
}


function BullpenBannerCard() {
  const [url, setUrl] = useState("");
  const [saved, setSaved] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/bullpen-banner").then(({ data }) => { setUrl(data.image_url || ""); setSaved(data.image_url || ""); }).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/bullpen-banner", { image_url: url.trim() });
      setSaved(data.image_url || ""); toast.success("Bullpen photo updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="card-surface rounded-md p-5 mb-6" data-testid="bullpen-banner-card">
      <div className="flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-gold" /><h3 className="font-display font-semibold text-white">General Bullpen photo</h3></div>
      <p className="text-xs text-white/50 mb-3">The banner shown on Bullpen event cards (homepage, Events, and the Next in the Bullpen section). Paste an image URL.</p>
      {saved && <img src={saved} alt="Bullpen banner" className="w-full max-w-md h-32 object-cover rounded-sm border border-white/10 mb-3" data-testid="bullpen-banner-preview" />}
      <div className="flex flex-col sm:flex-row gap-2 max-w-2xl">
        <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/image.jpg" data-testid="bullpen-banner-input" className="bg-charcoal border-white/15" />
        <Button onClick={save} disabled={saving || url.trim() === saved} data-testid="bullpen-banner-save-btn" className="shrink-0 bg-gold text-charcoal hover:bg-gold-hover">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
    </div>
  );
}

function AssistantAdmin() {
  const [email, setEmail] = useState("");
  const [acct, setAcct] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [newToken, setNewToken] = useState("");
  const [audit, setAudit] = useState([]);
  const loadTokens = () => api.get("/admin/api-tokens").then(({ data }) => setTokens(data)).catch(() => {});
  const loadAudit = () => api.get("/admin/audit-log?limit=40").then(({ data }) => setAudit(data)).catch(() => {});
  useEffect(() => { loadTokens(); loadAudit(); }, []);
  const createAcct = async () => {
    if (!email.trim()) { toast.error("Enter the AgentMail email"); return; }
    try {
      const { data } = await api.post("/admin/assistant-account", { email: email.trim(), name: "HSTC Community Assistant" });
      setAcct(data); toast.success("Assistant account ready");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const mint = async () => {
    if (!acct?.user_id) { toast.error("Create the assistant account first"); return; }
    try {
      const { data } = await api.post("/admin/api-tokens", { user_id: acct.user_id, name: "Agent token" });
      setNewToken(data.token); loadTokens(); toast.success("Token created — copy it now, it won't be shown again");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const revoke = async (tid) => { await api.delete(`/admin/api-tokens/${tid}`); loadTokens(); toast.success("Token revoked"); };
  return (
    <div className="card-surface rounded-md p-5 mb-6" data-testid="assistant-admin">
      <div className="flex items-center gap-2 mb-1"><Bot className="w-4 h-4 text-gold" /><h3 className="font-display font-semibold text-white">HSTC Community Assistant</h3></div>
      <p className="text-xs text-white/50 mb-3">A trusted operational (AI) account that manages case content &amp; community, with no owner/admin, security, or financial access. Create it with the AgentMail address, then mint a revocable API token for the agent.</p>
      <div className="flex flex-col sm:flex-row gap-2 max-w-xl mb-2">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="assistant@agentmail…" className="bg-charcoal border-white/15" data-testid="assistant-email-input" />
        <Button onClick={createAcct} className="shrink-0 bg-gold text-charcoal hover:bg-gold-hover" data-testid="create-assistant-btn">Create / promote</Button>
        <Button onClick={mint} variant="outline" className="shrink-0 border-white/15 text-white/80 bg-transparent" data-testid="mint-token-btn">Mint API token</Button>
      </div>
      {acct && <p className="text-xs text-gold mb-2">Account: {acct.name} · {acct.email} · role {acct.role}</p>}
      {newToken && (
        <div className="mb-3 p-3 rounded-sm border border-gold/40 bg-gold/10" data-testid="new-token-box">
          <p className="text-xs text-white/70 mb-1">Copy this token now — it is not shown again:</p>
          <code className="block text-xs text-gold break-all">{newToken}</code>
        </div>
      )}
      {tokens.length > 0 && (
        <div className="space-y-1.5 mb-4">
          {tokens.map((t) => (
            <div key={t.token_id} className="flex items-center justify-between gap-3 text-xs border border-white/10 rounded-sm px-3 py-2" data-testid={`token-${t.token_id}`}>
              <span className="text-white/70 truncate">{t.name} · created {(t.created_at || "").slice(0, 10)} · last used {t.last_used_at ? t.last_used_at.slice(0, 16).replace("T", " ") : "never"} {t.revoked && <span className="text-red-400">· revoked</span>}</span>
              {!t.revoked && <button onClick={() => revoke(t.token_id)} className="shrink-0 text-white/40 hover:text-red-400" data-testid={`revoke-${t.token_id}`}>Revoke</button>}
            </div>
          ))}
        </div>
      )}
      <div className="pt-3 border-t border-white/10">
        <div className="flex items-center justify-between mb-2"><span className="label-mono text-white/40">Activity log</span><button onClick={loadAudit} className="text-xs text-white/40 hover:text-gold">Refresh</button></div>
        {audit.length === 0 ? <p className="text-xs text-white/30">No activity yet.</p> : (
          <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="audit-log">
            {audit.map((a) => (
              <div key={a.audit_id} className="text-xs text-white/55 flex gap-2">
                <span className="text-white/30 shrink-0">{(a.created_at || "").slice(0, 16).replace("T", " ")}</span>
                <span className="text-gold shrink-0">{a.actor_name || a.actor_role}</span>
                <span className="truncate">{a.action} {a.target_type}{a.target_id ? ` (${a.target_id})` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AccountEmailCard() {
  const { user, refreshUser } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!email.trim() || email.trim() === user?.email) return;
    setSaving(true);
    try {
      await api.put("/me/email", { email: email.trim() });
      toast.success("Account email updated — your digest and alerts will go here now.");
      refreshUser?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="card-surface rounded-md p-5 mb-6" data-testid="account-email-card">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-4 h-4 text-gold" />
        <h3 className="font-display font-semibold text-white">Your account email</h3>
      </div>
      <p className="text-xs text-white/50 mb-3">This is where your weekly digest is delivered. Use an inbox that actually receives mail (a bouncing address won't get anything).</p>
      <div className="flex flex-col sm:flex-row gap-2 max-w-md">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="account-email-input" className="bg-charcoal border-white/15" />
        <Button onClick={save} disabled={saving || email.trim() === user?.email} data-testid="account-email-save-btn" className="shrink-0 bg-gold text-charcoal hover:bg-gold-hover">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
    </div>
  );
}

function EmailIssuesCard() {
  const [issues, setIssues] = useState([]);
  useEffect(() => { api.get("/admin/email-issues").then(({ data }) => setIssues(data.issues || [])).catch(() => {}); }, []);
  if (issues.length === 0) return null;
  return (
    <div className="card-surface rounded-md p-5 mb-6 border border-red-500/30" data-testid="email-issues-card">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-red-400" />
        <h3 className="font-display font-semibold text-white">Email delivery issues ({issues.length})</h3>
      </div>
      <p className="text-xs text-white/50 mb-3">These members' emails are bouncing — they're not receiving the digest or alerts. Nudge them to update their email.</p>
      <div className="divide-y divide-white/5">
        {issues.map((u) => (
          <div key={u.user_id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`email-issue-${u.user_id}`}>
            <div className="min-w-0">
              <div className="text-white truncate">{u.name || "—"}</div>
              <div className="text-white/50 font-mono text-xs truncate">{u.email}</div>
            </div>
            <span className="shrink-0 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm bg-red-500/15 text-red-300">{u.email_issue_type || "bounced"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const CATEGORIES = [
  { id: "active", name: "🔥 Active Cases" },
  { id: "missing", name: "🧍 Missing Persons" },
  { id: "unidentified", name: "🦴 Unidentified Human Remains" },
  { id: "cold", name: "❄️ Cold Cases" },
  { id: "wrongful", name: "⚖️ Wrongful Convictions" },
  { id: "dna", name: "🧬 DNA Cases" },
];

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [featuredSlug, setFeaturedSlug] = useState("");
  const [caseOpen, setCaseOpen] = useState(false);
  const [annOpen, setAnnOpen] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [usage, setUsage] = useState(null);
  const [visits, setVisits] = useState(null);
  const [signups, setSignups] = useState(null);
  const [showTest, setShowTest] = useState(false);
  const [cf, setCf] = useState({ title: "", category: "active", status: "Active", location: "", summary: "", featured: false });
  const [af, setAf] = useState({ title: "", body: "", images: [] });
  const [cpf, setCpf] = useState({ code: "", disc_type: "percent", value: "", max_redemptions: "", expires_at: "" });

  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/cases").then(({ data }) => setCases(data)).catch(() => {});
    api.get("/coupons").then(({ data }) => setCoupons(data)).catch(() => {});
    api.get("/admin/usage").then(({ data }) => setUsage(data)).catch(() => {});
    api.get("/admin/visits").then(({ data }) => setVisits(data)).catch(() => {});
    api.get("/admin/signups").then(({ data }) => setSignups(data)).catch(() => {});
    api.get("/settings/featured-case").then(({ data }) => setFeaturedSlug(data.slug)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const createCase = async () => {
    if (!cf.title || !cf.summary) { toast.error("Title and summary required"); return; }
    try { await api.post("/cases", cf); setCaseOpen(false); setCf({ title: "", category: "active", status: "Active", location: "", summary: "", featured: false }); load(); toast.success("Case created"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const postAnn = async () => {
    try { await api.post("/announcements", af); setAnnOpen(false); setAf({ title: "", body: "", images: [] }); toast.success("Announcement posted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createCoupon = async () => {
    if (!cpf.code || !cpf.value) { toast.error("Code and value required"); return; }
    const payload = {
      code: cpf.code,
      percent_off: cpf.disc_type === "percent" ? parseInt(cpf.value) : 0,
      amount_off: cpf.disc_type === "amount" ? parseFloat(cpf.value) : 0,
      free_trial_days: cpf.disc_type === "trial" ? parseInt(cpf.value) : 0,
      free_days: cpf.disc_type === "free_days" ? parseInt(cpf.value) : 0,
      academy_days: cpf.disc_type === "academy_days" ? parseInt(cpf.value) : 0,
      max_redemptions: cpf.max_redemptions ? parseInt(cpf.max_redemptions) : null,
      expires_at: cpf.expires_at || null,
      active: true,
    };
    try { await api.post("/coupons", payload); setCouponOpen(false); setCpf({ code: "", disc_type: "percent", value: "", max_redemptions: "", expires_at: "" }); load(); toast.success("Coupon created"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteCoupon = async (codeStr) => { await api.delete(`/coupons/${codeStr}`); load(); toast.success("Coupon deleted"); };
  const toggleFeature = async (c) => { await api.patch(`/cases/${c.case_id}`, { featured: !c.featured }); load(); };
  const saveFeatured = async (slug) => { setFeaturedSlug(slug); try { await api.put("/settings/featured-case", { slug }); toast.success("Homepage featured case updated"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const archive = async (c) => { await api.patch(`/cases/${c.case_id}`, { archived: true }); load(); toast.success("Case archived"); };

  const STAT_CARDS = stats ? [
    { icon: FolderPlus, label: "Cases", value: stats.cases },
    { icon: Users, label: "Members", value: stats.members },
    { icon: Star, label: "Subscribers", value: stats.active_subscribers },
    { icon: FileText, label: "Documents", value: stats.documents },
    { icon: MessagesSquare, label: "Threads", value: stats.threads },
  ] : [];

  return (
    <div className="min-h-screen bg-charcoal">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-2 mb-1"><Shield className="w-5 h-5 text-gold" /><span className="label-mono">Command Center</span></div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-8">Admin Console</h1>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          {STAT_CARDS.map((s) => (
            <div key={s.label} className="card-surface rounded-md p-5">
              <s.icon className="w-5 h-5 text-gold mb-3" />
              <div className="font-display text-3xl font-bold text-white">{s.value}</div>
              <div className="label-mono mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {visits && (
          <div className="mb-10" data-testid="visits-panel">
            <div className="flex items-center gap-2 mb-4"><Eye className="w-4 h-4 text-gold" /><h2 className="label-mono">Genuine Visitors</h2></div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: "Today · 24h", v: visits.visits.h24, u: visits.unique.h24 },
                { label: "This week · 7d", v: visits.visits.d7, u: visits.unique.d7 },
                { label: "This month · 30d", v: visits.visits.d30, u: visits.unique.d30 },
                { label: "All time", v: visits.visits.total, u: visits.unique.total },
              ].map((c) => (
                <div key={c.label} className="card-surface rounded-md p-5">
                  <Eye className="w-5 h-5 text-gold mb-3" />
                  <div className="font-display text-3xl font-bold text-white">{c.v.toLocaleString()}</div>
                  <div className="label-mono mt-1">{c.label}</div>
                  <div className="text-xs text-white/40 font-mono mt-2">{c.u.toLocaleString()} unique visitors</div>
                </div>
              ))}
            </div>
            <div className="card-surface rounded-md p-5 mt-3">
              <div className="label-mono mb-4">Visits · last 7 days</div>
              <div className="flex items-end justify-between gap-2 h-28">
                {visits.daily.map((d) => {
                  const max = Math.max(1, ...visits.daily.map((x) => x.count));
                  const h = Math.round((d.count / max) * 100);
                  const label = new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" });
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1.5 h-full">
                      <span className="text-xs font-mono text-white/60">{d.count}</span>
                      <div className="w-full rounded-sm bg-gold/70" style={{ height: `${Math.max(h, 3)}%` }} />
                      <span className="text-[10px] font-mono text-white/40">{label}</span>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-white/40 font-mono mt-3">
                Genuine visitor traffic only — admin, test, bot, and preview activity is excluded (but kept in the raw records).
                Free and paying members are counted. Unique visitors are de-duplicated per browser.
              </p>
              {visits.excluded && (
                <p className="text-xs text-white/30 font-mono mt-1" data-testid="visits-excluded-note">
                  Internal excluded: {(visits.excluded.d30 ?? 0).toLocaleString()} in the last 30 days · {(visits.excluded.total ?? 0).toLocaleString()} all-time
                </p>
              )}
            </div>
          </div>
        )}

        {usage && (
          <div className="mb-10" data-testid="usage-panel">
            <div className="flex items-center gap-2 mb-4"><Activity className="w-4 h-4 text-gold" /><h2 className="label-mono">Usage &amp; Costs <span className="text-white/30">· for pricing</span></h2></div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="card-surface rounded-md p-5">
                <Activity className="w-5 h-5 text-gold mb-3" />
                <div className="font-display text-3xl font-bold text-white" data-testid="usage-requests-24h">{usage.requests.h24.toLocaleString()}</div>
                <div className="label-mono mt-1">API calls · 24h</div>
                <div className="text-xs text-white/40 font-mono mt-2">{usage.requests.d7.toLocaleString()} / 7d · {usage.requests.d30.toLocaleString()} / 30d</div>
              </div>
              <div className="card-surface rounded-md p-5">
                <Bot className="w-5 h-5 text-gold mb-3" />
                <div className="font-display text-3xl font-bold text-white" data-testid="usage-ai-calls">{usage.ai.total_calls.toLocaleString()}</div>
                <div className="label-mono mt-1">AI summaries (total)</div>
                <div className="text-xs text-white/40 font-mono mt-2">{usage.ai.calls_30d.toLocaleString()} in last 30d</div>
              </div>
              <div className="card-surface rounded-md p-5">
                <DollarSign className="w-5 h-5 text-gold mb-3" />
                <div className="font-display text-3xl font-bold text-white" data-testid="usage-ai-cost">${usage.ai.est_cost_usd.toFixed(2)}</div>
                <div className="label-mono mt-1">Est. AI cost (all-time)</div>
                <div className="text-xs text-white/40 font-mono mt-2">~${usage.ai.cost_per_subscriber.toFixed(3)} / subscriber · {(usage.ai.input_tokens + usage.ai.output_tokens).toLocaleString()} tokens</div>
              </div>
              <div className="card-surface rounded-md p-5">
                <DollarSign className="w-5 h-5 text-green-400 mb-3" />
                <div className="font-display text-3xl font-bold text-white" data-testid="usage-mrr">${usage.revenue.est_mrr_usd.toLocaleString()}</div>
                <div className="label-mono mt-1">Est. MRR</div>
                <div className="text-xs text-white/40 font-mono mt-2">{usage.revenue.monthly} monthly · {usage.revenue.annual} annual</div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <div className="card-surface rounded-md p-5">
                <div className="label-mono mb-3">Busiest endpoints · 7d</div>
                <div className="space-y-2">
                  {usage.top_routes.map((r) => (
                    <div key={r.route} className="flex items-center justify-between text-sm">
                      <span className="font-mono text-white/70 truncate">{r.route}</span>
                      <span className="font-mono text-gold">{r.count.toLocaleString()}</span>
                    </div>
                  ))}
                  {usage.top_routes.length === 0 && <p className="text-sm text-white/40">No traffic logged yet.</p>}
                </div>
              </div>
              <div className="card-surface rounded-md p-5 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className={`w-4 h-4 ${usage.errors_24h > 0 ? "text-red-400" : "text-white/30"}`} />
                  <span className="label-mono">Server errors · 24h</span>
                </div>
                <div className={`font-display text-3xl font-bold ${usage.errors_24h > 0 ? "text-red-400" : "text-white"}`} data-testid="usage-errors">{usage.errors_24h}</div>
                <p className="text-xs text-white/40 font-mono mt-2">AI cost estimate uses gpt-5.4-mini token rates and is approximate — use it as a directional signal for pricing, not an invoice.</p>
              </div>
            </div>
          </div>
        )}

        {signups && (
          <div className="mb-10" data-testid="signups-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-gold" /><h2 className="label-mono">Who&apos;s Signing Up</h2></div>
              <div className="flex items-center gap-3">
                <span className="label-mono" data-testid="signup-counts">
                  <span className="text-green-400">{signups.counts?.real ?? 0} real</span>
                  <span className="text-white/30"> · </span>
                  <span className="text-amber-300">{signups.counts?.test ?? 0} test</span>
                </span>
                <button onClick={() => setShowTest((v) => !v)} data-testid="toggle-test-accounts"
                  className={`label-mono px-2.5 py-1 rounded-sm border transition-colors ${showTest ? "border-amber-300/40 text-amber-300 bg-amber-300/5" : "border-white/10 text-white/50 hover:border-gold/40"}`}>
                  {showTest ? "Hide test accounts" : "Show test accounts"}
                </button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="card-surface rounded-md p-5">
                <div className="label-mono mb-3">New members <span className="text-white/30">· real{showTest ? " + test" : ""}</span></div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {(() => {
                    const list = signups.recent_members.filter((u) => showTest || !u.is_test);
                    if (list.length === 0) return <p className="text-sm text-white/40">No {showTest ? "" : "real "}members yet.</p>;
                    return list.map((u, i) => (
                      <div key={u.email || i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0">
                        <div className="min-w-0">
                          <span className="text-white font-medium truncate">{u.name || u.email}</span>
                          {u.is_test && <span className="ml-2 text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-amber-300/15 text-amber-300 align-middle" data-testid="test-badge">TEST</span>}
                          <div className="text-xs text-white/40 font-mono truncate">{u.email}</div>
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${u.subscription_status === "active" ? "bg-green-400/15 text-green-400" : "bg-white/10 text-white/50"}`}>{u.subscription_status === "active" ? (u.subscription_plan || "active") : "free"}</span>
                          <div className="text-[10px] text-white/30 font-mono mt-0.5">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
              <div className="card-surface rounded-md p-5">
                <div className="label-mono mb-3">Paid subscriptions <span className="text-white/30">· latest</span></div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {(() => {
                    const list = signups.recent_subscriptions.filter((s) => showTest || !s.is_test);
                    if (list.length === 0) return <p className="text-sm text-white/40">No paid subscriptions yet.</p>;
                    return list.map((s, i) => (
                      <div key={`${s.email}-${s.created_at}-${i}`} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0">
                        <div className="min-w-0">
                          <span className="text-white font-medium truncate">{s.email}</span>
                          {s.is_test && <span className="ml-2 text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-amber-300/15 text-amber-300 align-middle">TEST</span>}
                          <div className="text-xs text-white/40 font-mono">{s.plan || "—"}{s.coupon ? ` · ${s.coupon}` : ""}</div>
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <span className="text-sm font-mono text-green-400">${(s.amount || 0).toFixed(2)}</span>
                          <div className="text-[10px] text-white/30 font-mono mt-0.5">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3 mb-8">
          <Dialog open={caseOpen} onOpenChange={setCaseOpen}>
            <DialogTrigger asChild><Button data-testid="create-case-btn" className="bg-gold text-charcoal hover:bg-gold-hover rounded-sm"><FolderPlus className="w-4 h-4 mr-1" /> Create Case Workspace</Button></DialogTrigger>
            <DialogContent className="bg-surface border-white/10 text-white max-w-lg">
              <DialogHeader><DialogTitle className="font-display">New Case Workspace</DialogTitle></DialogHeader>
              <Input placeholder="Case title" value={cf.title} onChange={(e) => setCf({ ...cf, title: e.target.value })} className="bg-charcoal border-white/15" data-testid="case-title-input" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={cf.category} onValueChange={(v) => setCf({ ...cf, category: v })}>
                  <SelectTrigger className="bg-charcoal border-white/15"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white">{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Status (e.g. Active)" value={cf.status} onChange={(e) => setCf({ ...cf, status: e.target.value })} className="bg-charcoal border-white/15" />
              </div>
              <Input placeholder="Location" value={cf.location} onChange={(e) => setCf({ ...cf, location: e.target.value })} className="bg-charcoal border-white/15" />
              <Textarea placeholder="Case summary" rows={5} value={cf.summary} onChange={(e) => setCf({ ...cf, summary: e.target.value })} className="bg-charcoal border-white/15" data-testid="case-summary-input" />
              <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={cf.featured} onChange={(e) => setCf({ ...cf, featured: e.target.checked })} /> Feature on homepage</label>
              <DialogFooter><Button onClick={createCase} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="save-case-btn">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={annOpen} onOpenChange={setAnnOpen}>
            <DialogTrigger asChild><Button data-testid="create-announcement-btn" variant="outline" className="border-white/15 text-white hover:border-gold/40 rounded-sm bg-transparent"><Megaphone className="w-4 h-4 mr-1" /> Post Announcement</Button></DialogTrigger>
            <DialogContent className="bg-surface border-white/10 text-white">
              <DialogHeader><DialogTitle className="font-display">New Announcement</DialogTitle></DialogHeader>
              <Input placeholder="Title" value={af.title} onChange={(e) => setAf({ ...af, title: e.target.value })} className="bg-charcoal border-white/15" data-testid="announcement-title-input" />
              <Textarea placeholder="Message" rows={4} value={af.body} onChange={(e) => setAf({ ...af, body: e.target.value })} className="bg-charcoal border-white/15" data-testid="announcement-body-input" />
              <ImageUploader images={af.images} setImages={(imgs) => setAf({ ...af, images: imgs })} />
              <DialogFooter><Button onClick={postAnn} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="post-announcement-submit">Post</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={couponOpen} onOpenChange={setCouponOpen}>
            <DialogTrigger asChild><Button data-testid="create-coupon-btn" variant="outline" className="border-white/15 text-white hover:border-gold/40 rounded-sm bg-transparent"><Ticket className="w-4 h-4 mr-1" /> Coupons</Button></DialogTrigger>
            <DialogContent className="bg-surface border-white/10 text-white">
              <DialogHeader><DialogTitle className="font-display">Create Coupon Code</DialogTitle></DialogHeader>
              <Input placeholder="CODE (e.g. FOUNDING50)" value={cpf.code} onChange={(e) => setCpf({ ...cpf, code: e.target.value.toUpperCase() })} className="bg-charcoal border-white/15 uppercase tracking-wider" data-testid="coupon-code-input" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={cpf.disc_type} onValueChange={(v) => setCpf({ ...cpf, disc_type: v })}>
                  <SelectTrigger className="bg-charcoal border-white/15"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white">
                    <SelectItem value="percent">% off</SelectItem>
                    <SelectItem value="amount">$ off</SelectItem>
                    <SelectItem value="trial">Free trial (days)</SelectItem>
                    <SelectItem value="free_days">Free days (no card)</SelectItem>
                    <SelectItem value="academy_days">Academy access (days)</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="number" placeholder={cpf.disc_type === "percent" ? "e.g. 50 (100 = free)" : cpf.disc_type === "trial" ? "e.g. 30 days free" : cpf.disc_type === "free_days" ? "e.g. 7 (days free, no card)" : cpf.disc_type === "academy_days" ? "e.g. 30 (Academy days, no card)" : "e.g. 5.00"} value={cpf.value} onChange={(e) => setCpf({ ...cpf, value: e.target.value })} className="bg-charcoal border-white/15" data-testid="coupon-value-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" placeholder="Max redemptions (blank = ∞)" value={cpf.max_redemptions} onChange={(e) => setCpf({ ...cpf, max_redemptions: e.target.value })} className="bg-charcoal border-white/15" />
                <Input type="date" value={cpf.expires_at} onChange={(e) => setCpf({ ...cpf, expires_at: e.target.value })} className="bg-charcoal border-white/15" />
              </div>
              <p className="text-[11px] text-white/40 font-mono">Free days (no card) = full access for N days with NO credit card; access just ends after that, never charged. Free trial (days) = free for N days, then the card is auto-charged. 100% off = one free period (no card). Academy access (days) = unlocks ONLY the Detective Academy for N days (no card, no full membership) — members redeem it on the Academy page.</p>
              <DialogFooter><Button onClick={createCoupon} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="save-coupon-btn">Create Coupon</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {coupons.length > 0 && (
          <>
            <h2 className="label-mono mb-4">Coupon Codes</h2>
            <div className="space-y-2 mb-10">
              {coupons.map((c) => (
                <div key={c.code} className="card-surface rounded-md p-4 flex items-center justify-between gap-3" data-testid={`coupon-${c.code}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-sm bg-gold/10 border border-gold/20 flex items-center justify-center"><Ticket className="w-4 h-4 text-gold" /></div>
                    <div>
                      <div className="font-mono font-semibold text-white tracking-wider">{c.code}</div>
                      <div className="label-mono">
                        {c.academy_days ? `Academy access — ${c.academy_days} days (no card)` : c.free_days ? `${c.free_days} days free (no card)` : c.free_trial_days ? `${c.free_trial_days}-day free trial` : c.percent_off ? `${c.percent_off}% off` : `$${Number(c.amount_off).toFixed(2)} off`}
                        {" · "}{c.redeemed_count || 0}{c.max_redemptions ? `/${c.max_redemptions}` : ""} used
                        {c.expires_at ? ` · exp ${c.expires_at.slice(0, 10)}` : ""}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => deleteCoupon(c.code)} className="text-white/30 hover:text-red-400 transition-colors" data-testid={`delete-coupon-${c.code}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="card-surface rounded-md p-5 mb-6">
          <div className="flex items-center gap-2 mb-1"><Star className="w-4 h-4 text-gold" /><h3 className="font-display font-semibold text-white">Homepage Featured Case</h3></div>
          <p className="text-xs text-white/50 mb-3">Controls the case spotlighted in the homepage &quot;Featured case&quot; section. Change it anytime — no code needed.</p>
          <Select value={featuredSlug} onValueChange={saveFeatured}>
            <SelectTrigger data-testid="featured-case-select" className="bg-charcoal border-white/15 max-w-md"><SelectValue placeholder="Choose a case" /></SelectTrigger>
            <SelectContent className="bg-surface border-white/10 text-white max-h-72">
              {cases.map((c) => <SelectItem key={c.slug} value={c.slug}>{caseDisplayName(c.title)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <YouTubeSync cases={cases} />

        <VideoLibraryAdmin />

        <AccountEmailCard />

        <CompMemberCard />

        <PastBullpensAdmin />

        <BullpenBannerCard />

        <FeaturedEventCard />

        <AssistantAdmin />

        <AssistantActivityCard />

        <AcademyAdmin />

        <DigestCard />

        <EmailIssuesCard />

        <h2 className="label-mono mb-4">Manage Cases</h2>
        <div className="space-y-3">
          {cases.map((c) => (
            <div key={c.case_id} className="card-surface rounded-md p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <img src={c.thumbnail} alt="" className="w-12 h-12 rounded-sm object-cover" />
                <div>
                  <Link to={`/cases/${c.slug}`} className="font-display font-semibold text-white hover:text-gold transition-colors">{caseDisplayName(c.title)}</Link>
                  <div className="label-mono">{c.status} · {c.thread_count} threads · {c.follower_count} followers</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleFeature(c)} data-testid={`feature-${c.slug}`}
                  className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border transition-colors ${c.featured ? "bg-gold/15 text-gold border-gold/40" : "border-white/10 text-white/60 hover:border-gold/40"}`}>
                  <Star className={`w-3.5 h-3.5 ${c.featured ? "fill-gold" : ""}`} /> {c.featured ? "Featured" : "Feature"}
                </button>
                <Link to={`/cases/${c.slug}`} className="text-xs px-3 py-2 rounded-sm border border-white/10 text-white/70 hover:border-gold/40 transition-colors">Open</Link>
                <button onClick={() => archive(c)} className="text-xs px-3 py-2 rounded-sm border border-white/10 text-white/50 hover:text-red-400 hover:border-red-400/40 transition-colors" data-testid={`archive-${c.slug}`}><Archive className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
